
import { useNavigate } from 'react-router-dom';

import AddRegionForm from './AddRegionForm';
import AddVegForm from './AddVegForm';
import DangerZone from '../../components/DangerZone';
import PhenotypeEditor from '../../components/PhenotypeEditor';
import PhotoSection from '../../components/PhotoSection';
import QRCodeField from '../../components/QRCodeField';
import SuggestionList, { regionRow, speciesRow } from './SuggestionList';
import { CONFIDENCE, IDENT_METHODS, LAB_STATUSES, RANKS, SIZES, STAGES, SUBSTRATES,
  GROWTH_PATTERNS } from './fieldOptions';
import { btnStyle, dimStyle, errStyle, gridStyle, inputStyle, labelStyle,
  modalStyle, overlayStyle, subFormStyle, tdStyle, thStyle } from './styles';
import OrganicLoader from '../../components/organic/OrganicLoader.jsx';
import { AttachedBioProfile } from '../../components/bioprofile/BioProfile';
import { displayName } from '../../utils/userLabel';
import CollectionShare from '../../components/share/CollectionShare';

// A section of the open record still loading: a growing tip beside the words.
// Not announced -- the record itself is already on screen.
const SectionLoader = ({ t }) => (
  <p style={dimStyle}>
    <OrganicLoader inline showLabel announce={false} label={t('common:loading')} slowLabel={t('common:stillWorking')} />
  </p>
);

/*
 * Everything held under this sample: plates, slants, DNA isolates and
 * sequencing runs. From main (0dab3ea), where it lived in the 2,723-line
 * Collections.js; here it is a component beside the modal that shows it.
 *
 * Why it matters: a sample's own QR code is the one on the bag somebody
 * actually scans in the lab. Before this, reaching its plates meant knowing
 * to go and look for them.
 *
 * Each code is its own `dir="ltr"` span with the label outside it. A code is
 * Latin text and a Hebrew line is RTL, so an unwrapped `Slant: ABC-SA` puts
 * the label on the wrong end of the row -- main's own follow-up fix
 * (6444668), kept.
 */
function RelatedRecords({ t, family }) {
  const navigate = useNavigate();
  if (!family) return null;
  // Every list defaults to empty. main read `family.plates.length` directly,
  // which is fine against its own endpoint and not fine against a response
  // that is older, partial, or an error body -- there it throws inside a
  // render and takes the whole detail modal down. Found by the Collections
  // suite, whose api mock answers /family without these keys.
  const list = name => (Array.isArray(family[name]) ? family[name] : []);
  const rows = [
    ...list('plates').map(r => ({ key: `p${r.id}`, label: t('detail.plateLabel'), code: r.code,
                                 to: ['/plates', { openPlateId: r.id }] })),
    ...list('slants').map(r => ({ key: `s${r.id}`, label: t('detail.slantLabel'), code: r.code,
                                 to: ['/slants', { openSlantId: r.id }] })),
    ...list('dna').map(r => ({ key: `d${r.id}`, label: t('detail.dnaLabel'), code: r.code,
                              to: ['/dna', { openDnaId: r.id }] })),
    ...list('runs').map(r => ({ key: `r${r.id}`, label: t('detail.runLabel'),
                              code: `${r.gene_name} — ${r.code}`,
                              to: ['/dna', { openRunId: r.id }] })),
  ];
  if (rows.length === 0) return null;
  return (
    <div data-testid="related-records"
         style={{ marginTop: 16, borderTop: '1px solid var(--c-border)', paddingTop: 12 }}>
      <h4 style={{ margin: '0 0 8px', fontSize: 14, color: 'var(--c-text-fg)' }}>
        {t('detail.relatedRecords')}
      </h4>
      {rows.map(row => (
        <div key={row.key} style={{ cursor: 'pointer', fontSize: 13, padding: '2px 0' }}
             onClick={() => navigate(row.to[0], { state: row.to[1] })} title={t('tip.relatedRecord')}>
          <span>{row.label}</span> <span dir="ltr">{row.code}</span>
        </div>
      ))}
    </div>
  );
}

/** A titled block inside the modal with its own add button. */
function SubSection({ title, onAdd, addLabel, addTitle, children }) {
  return (
    <div style={{ marginTop: 20, borderTop: '1px solid var(--c-border)', paddingTop: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <strong style={{ fontSize: 14 }}>{title}</strong>
        <button onClick={onAdd} title={addTitle} style={{ ...btnStyle('var(--c-primary)'), padding: '5px 12px', fontSize: 13 }}>{addLabel}</button>
      </div>
      {children}
    </div>
  );
}

/**
 * One collection, opened: what is on the record, and the form for changing it.
 *
 * Everything it shows and everything it does comes off `detailView`
 * (collections/useCollectionDetail.js), so this file is the shape of the
 * modal and nothing else.
 */
export default function CollectionDetailModal({
  t, user, canEdit, isCollector, detailView, onResumeDraft,
}) {
  return (
    <>
      {(detailView.detailLoading || detailView.detail) && (
        <div style={overlayStyle} onClick={detailView.close}>
          <div role="dialog" aria-modal="true" style={modalStyle} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
              <div>
                <h3 style={{ margin: 0, fontStyle: detailView.detail?.current_taxon_name ? 'italic' : 'normal', color: 'var(--c-primary-fg)' }} dir={detailView.detail?.current_taxon_name ? 'ltr' : undefined}>
                  {detailView.detail?.current_taxon_name || t('detail.title')}
                </h3>
                <span style={{ fontSize: 12, color: 'var(--c-text-muted-fg)', fontFamily: 'monospace' }} dir="ltr">{detailView.detail?.sample_id}</span>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {/* Share or export this finding (2026-10-19): what this reader was sent, nothing more. */}
                {detailView.detail && !detailView.editing && (
                  <CollectionShare collection={detailView.detail} identifications={detailView.identHistory}
                    vegetation={detailView.vegetation} />
                )}
                {canEdit && detailView.detail && !detailView.editing && <button onClick={detailView.startEdit} data-testid="detail-edit" title={t('tip.editCollection')} style={btnStyle('var(--c-primary)')}>{t('detail.editBtn')}</button>}
                {detailView.detail?.is_draft && ((detailView.detail?.collector_id === user?.user_id && isCollector) || canEdit) && !detailView.editing && (
                  <button onClick={detailView.recaptureGPS} title={t('tip.recaptureGps')} style={btnStyle('var(--c-info)')}>{t('detail.recaptureGps')}</button>
                )}
                {detailView.detail?.is_draft && ((detailView.detail?.collector_id === user?.user_id && isCollector) || canEdit) && !detailView.editing && (
                  <button onClick={() => { detailView.close(); onResumeDraft(detailView.detail.sample_id); }} title={t('tip.continueDraft')} style={btnStyle('var(--c-primary)')}>
                    {t('wizard.continueDraftBtn')}
                  </button>
                )}
                <button onClick={detailView.close} title={t('tip.closeDetail')} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--c-text-muted-fg)' }}>×</button>
              </div>
            </div>

            {detailView.detailLoading && (
              <OrganicLoader block motif="soil" label={t('common:loading')} slowLabel={t('common:stillWorking')} />
            )}

            {detailView.detail && !detailView.editing && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '8px 24px', fontSize: 14 }}>
                  {/* The code, what it is and who recorded it, as the map's
                      drawer says them (GET /collections/{id}'s summary). The
                      record's own QR only when a specimen's code is shown
                      instead of it. */}
                  {[
                    [t('detail.code'),               detailView.detail.code, 'ltr'],
                    [t('detail.qrUid'),              detailView.detail.qr_uid && detailView.detail.qr_uid !== detailView.detail.code ? detailView.detail.qr_uid : null, 'ltr'],
                    [t('detail.kind'),               detailView.detail.kind ? t(`map:kind.${detailView.detail.kind}`) : null],
                    [t('map:drawer.observer'),       detailView.detail.observer ? displayName(detailView.detail.observer, t) : null],
                    [t('detail.date'),               detailView.detail.collection_datetime ? new Date(detailView.detail.collection_datetime).toLocaleString() : null],
                    [t('detail.region'),             detailView.detail.region ? t(detailView.detail.region, { ns: 'regions' }) : null],
                    [t('detail.substrate'),          detailView.detail.substrate ? t(`substrate.${detailView.detail.substrate}`) : null],
                    [t('detail.fruitingBodySize'), detailView.detail.fruiting_body_size ? t(`size.${detailView.detail.fruiting_body_size}`) : null],
                    [t('detail.sizeEstimated'),     detailView.detail.fruiting_body_size_estimated ? t('common:yes') : null],
                    [t('detail.stage'),              detailView.detail.phenological_stage ? t(`stage.${detailView.detail.phenological_stage}`) : null],
                    [t('detail.count'),              detailView.detail.fruiting_body_count ?? null],
                    [t('detail.growthPattern'),     detailView.detail.growth_pattern ? t(`growthPattern.${detailView.detail.growth_pattern}`) : null],
                    [t('detail.gps'),                detailView.detail.gps_lat ? `${detailView.detail.gps_lat}, ${detailView.detail.gps_lng}` : null, 'ltr'],
                    [t('detail.taxon'),              detailView.detail.current_taxon_name, 'ltr'],
                    [t('detail.rank'),               detailView.detail.current_rank ? t(`common:rank.${detailView.detail.current_rank}`) : null],
                    [t('detail.confidence'),         detailView.detail.current_confidence ? t(`common:confidence.${detailView.detail.current_confidence}`) : null],
                    [t('detail.labStatus'),         detailView.detail.lab_status ? t(`labStatus.${detailView.detail.lab_status}`) : null],
                    [t('detail.draft'),              detailView.detail.is_draft != null ? (detailView.detail.is_draft ? t('common:yes') : t('common:no')) : null],
                    [t('detail.publicSubmission'),  detailView.detail.is_public_submission != null ? (detailView.detail.is_public_submission ? t('common:yes') : t('common:no')) : null],
                  ].map(([label, val, dir]) => val != null ? (
                    <div key={label}>
                      <span style={{ color: 'var(--c-text-muted-fg)' }}>{label}: </span>
                      <span style={{ fontStyle: label === t('detail.taxon') ? 'italic' : 'normal' }} dir={dir}>{val}</span>
                    </div>
                  ) : null)}
                  {detailView.detail.general_notes && (
                    <div style={{ gridColumn: 'span 2' }}>
                      <span style={{ color: 'var(--c-text-muted-fg)' }}>{t('detail.notes')}: </span>{detailView.detail.general_notes}
                    </div>
                  )}
                </div>

                <SubSection title={t('detail.vegetationTitle')}
                  onAdd={detailView.toggleVegForm} addTitle={t('tip.toggleVegForm')}
                  addLabel={detailView.showVegForm ? t('common:cancel') : t('common:add')}>
                  {detailView.showVegForm && (
                    <form onSubmit={detailView.submitVeg} style={subFormStyle}>
                      {detailView.vegError && <p style={errStyle}>{detailView.vegError}</p>}
                      <div style={{ position: 'relative', marginBottom: 8, maxWidth: 320 }}>
                        <label style={labelStyle}>
                          {t('detail.vegetationSpeciesLabel')}
                          <input
                            value={detailView.editVeg.query}
                            onChange={detailView.onEditVegInput}
                            onFocus={() => { if (detailView.editVeg.suggestions.length > 0) detailView.editVeg.show(); }}
                            onBlur={() => setTimeout(() => detailView.editVeg.close(), 150)}
                            placeholder={t('step2.vegSearchPlaceholder')}
                            title={t('tip.vegSearch')}
                            style={{ ...inputStyle, width: 260 }}
                            autoComplete="off"
                            dir="ltr"
                          />
                        </label>
                        <SuggestionList box={detailView.editVeg}
                          onPick={detailView.onEditVegPick}>{speciesRow}</SuggestionList>
                      </div>
                      {detailView.editVeg.failed && (
                        <span style={{ display: 'block', marginBottom: 8, fontSize: 12, color: 'var(--c-danger-fg)' }}>
                          {t('step2.vegSearchError')}
                        </span>
                      )}
                      {detailView.editVeg.noMatch && detailView.editVeg.query.trim().length >= 2 && (
                        <div style={{ marginBottom: 8 }}>
                          <span style={{ display: 'block', fontSize: 12, color: 'var(--c-danger-fg)', marginBottom: 4 }}>
                            {t('step2.vegNoMatch')}
                          </span>
                          {canEdit && !detailView.addingVeg && (
                            <button type="button" onClick={() => detailView.setAddingVeg(true)} onMouseDown={e => e.preventDefault()} title={t('tip.addNewSpecies')}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-primary-fg)', fontSize: 12, padding: 0, textDecoration: 'underline' }}>
                              {t('step2.addVegPrompt', { query: detailView.editVeg.query })}
                            </button>
                          )}
                        </div>
                      )}
                      {detailView.addingVeg && (
                        <AddVegForm t={t} initialQuery={detailView.editVeg.query} seqRef={detailView.editVegSeqRef}
                          onPicked={detailView.onEditVegPick} style={{ maxWidth: 320 }}
                          onClose={() => detailView.setAddingVeg(false)} />
                      )}
                      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                        <label style={{ ...labelStyle, flexDirection: 'row', alignItems: 'center', gap: 6, paddingBottom: 6 }}>
                          <input type="checkbox" name="is_dominant" checked={detailView.vegForm.is_dominant} onChange={detailView.onVegChange} title={t('tip.vegDominant')} />
                          {t('step2.dominant')}
                        </label>
                        <button type="submit" disabled={detailView.vegSaving || !detailView.vegForm.species} title={t('tip.saveVegetation')} style={{ ...btnStyle('var(--c-primary)'), alignSelf: 'flex-end' }}>
                          {detailView.vegSaving ? t('common:saving') : t('common:add')}
                        </button>
                      </div>
                    </form>
                  )}
                  {detailView.vegLoading ? <SectionLoader t={t} /> : detailView.vegetation.length === 0 ? (
                    <p style={dimStyle}>{t('detail.vegetationEmpty')}</p>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                      {detailView.vegetation.map(v => (
                        <span key={v.id} style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          background: v.is_dominant ? 'var(--c-success-soft)' : 'var(--c-bg-muted)',
                          color: v.is_dominant ? 'var(--c-success-fg)' : 'var(--c-text-body-fg)',
                          border: `1px solid ${v.is_dominant ? 'var(--c-primary-border-3)' : 'var(--c-border)'}`,
                          borderRadius: 16, padding: '3px 10px', fontSize: 13, fontStyle: 'italic',
                        }}>
                          {v.species}{v.hebrew_name ? ` (${v.hebrew_name})` : (v.identification_type ? ` (${t(`step2.vegIdType.${v.identification_type}`)})` : '')}{v.is_dominant ? ' ★' : ''}
                          {canEdit && (
                            <button onClick={() => detailView.setDeleteVegTarget(v.id)} title={t('tip.removeVegetation')}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-text-faint-fg)', fontSize: 14, padding: '0 2px', lineHeight: 1 }}>×</button>
                          )}
                        </span>
                      ))}
                    </div>
                  )}
                </SubSection>

                <PhotoSection
                  fetchUrl={`/collections/${detailView.detail.sample_id}/photos`}
                  uploadBody={{ sample_id: detailView.detail.sample_id }}
                  taxonName={detailView.detail.current_taxon_name}
                  sourceType="field_collection"
                  annotatable
                />

                <PhenotypeEditor anchor="collections" recordId={detailView.detail.sample_id}
                                 colourChange={detailView.detail.colour_change} canEdit={canEdit} />

                <SubSection title={t('detail.identHistoryTitle')}
                  onAdd={detailView.toggleIdentForm} addTitle={t('tip.toggleIdentForm')}
                  addLabel={detailView.showIdentForm ? t('common:cancel') : t('common:add')}>
                  {canEdit && detailView.detail.current_taxon_name && detailView.detail.current_rank && detailView.detail.current_rank !== 'unknown' && (
                    detailView.identHistory[0]?.expert_approval &&
                    detailView.identHistory[0]?.taxon_name === detailView.detail.current_taxon_name &&
                    detailView.identHistory[0]?.taxonomic_rank === detailView.detail.current_rank &&
                    detailView.identHistory[0]?.confidence === detailView.detail.current_confidence ? (
                      <p style={{ margin: '0 0 10px', fontSize: 13, color: 'var(--c-success-fg)', fontWeight: 500 }}>
                        {t('detail.identConfirmed')}
                      </p>
                    ) : (
                      <div style={{ background: 'var(--c-info-soft-2)', border: '1px solid var(--c-info-border)', borderRadius: 8, padding: '10px 14px', marginBottom: 10 }}>
                        {detailView.confirmIdentError && <p style={{ ...errStyle, marginBottom: 8 }}>{detailView.confirmIdentError}</p>}
                        <p style={{ margin: '0 0 8px', fontSize: 13, color: 'var(--c-info-3-fg)' }}>
                          {t('detail.identConfirmPrompt', {
                            taxon: detailView.detail.current_taxon_name,
                            rank: t(`common:rank.${detailView.detail.current_rank}`),
                            confidence: detailView.detail.current_confidence ? t(`common:confidence.${detailView.detail.current_confidence}`) : '—',
                          })}
                        </p>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button type="button" onClick={detailView.confirmIdentification} disabled={detailView.confirmingIdent} title={t('tip.confirmIdent')} style={btnStyle('var(--c-primary)')}>
                            {detailView.confirmingIdent ? t('common:saving') : t('detail.identConfirmBtn')}
                          </button>
                          <button type="button" onClick={detailView.openIdentForm} title={t('tip.needsReview')} style={btnStyle('var(--c-text-muted)')}>
                            {t('detail.identNeedsReviewBtn')}
                          </button>
                        </div>
                      </div>
                    )
                  )}
                  {detailView.showIdentForm && (
                    <form onSubmit={detailView.submitIdent} style={subFormStyle}>
                      {detailView.identError && <p style={errStyle}>{detailView.identError}</p>}
                      <div style={gridStyle}>
                        <label style={labelStyle}>{t('detail.identTaxonName')}
                          <input name="taxon_name" value={detailView.identForm.taxon_name} onChange={detailView.onIdentChange} style={{ ...inputStyle, fontStyle: 'italic' }} dir="ltr" title={t('tip.identTaxonName')} />
                        </label>
                        <label style={labelStyle}>{t('detail.identRank')}
                          <select name="taxonomic_rank" value={detailView.identForm.taxonomic_rank} onChange={detailView.onIdentChange} style={inputStyle} title={t('tip.identRank')}>
                            <option value="">{t('common:selectPlaceholder')}</option>
                            {RANKS.map(r => <option key={r} value={r}>{t(`common:rank.${r}`)}</option>)}
                          </select>
                        </label>
                        <label style={labelStyle}>{t('detail.identConfidence')}
                          <select name="confidence" value={detailView.identForm.confidence} onChange={detailView.onIdentChange} style={inputStyle} title={t('tip.identConfidence')}>
                            <option value="">{t('common:selectPlaceholder')}</option>
                            {CONFIDENCE.map(c => <option key={c} value={c}>{t(`common:confidence.${c}`)}</option>)}
                          </select>
                        </label>
                        <label style={labelStyle}>{t('detail.identMethod')}
                          <select name="identification_method" value={detailView.identForm.identification_method} onChange={detailView.onIdentChange} style={inputStyle} title={t('tip.identMethod')}>
                            <option value="">{t('common:selectPlaceholder')}</option>
                            {IDENT_METHODS.map(m => <option key={m} value={m}>{t(`common:identMethod.${m}`)}</option>)}
                          </select>
                        </label>
                        <label style={{ ...labelStyle, flexDirection: 'row', alignItems: 'center', gap: 8, alignSelf: 'flex-end' }}>
                          <input type="checkbox" checked={detailView.identForm.expert_approval} title={t('tip.identExpertApproved')}
                            onChange={e => detailView.setIdentForm(f => ({ ...f, expert_approval: e.target.checked }))} />
                          {t('detail.identExpertApproved')}
                        </label>
                        <label style={{ ...labelStyle, gridColumn: 'span 2' }}>{t('common:notes')}
                          <textarea name="notes" value={detailView.identForm.notes} onChange={detailView.onIdentChange} rows={2} style={inputStyle} title={t('tip.identNotes')} />
                        </label>
                      </div>
                      <button type="submit" disabled={detailView.identSaving} title={t('tip.saveIdent')} style={btnStyle('var(--c-primary)')}>
                        {detailView.identSaving ? t('common:saving') : t('detail.identAdd')}
                      </button>
                    </form>
                  )}
                  {detailView.identLoading ? <SectionLoader t={t} /> : detailView.identHistory.length === 0 ? (
                    <p style={dimStyle}>{t('detail.identHistoryEmpty')}</p>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginTop: 8 }}>
                      <thead>
                        <tr style={{ background: 'var(--c-bg-muted-2)' }}>
                          {[t('detail.colDate'), t('detail.colTaxon'), t('detail.colRank'), t('detail.colConfidence'), t('detail.colMethod'), t('detail.colApproved')].map(h => (
                            <th key={h} style={{ ...thStyle, padding: '6px 10px', fontSize: 12 }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {detailView.identHistory.map(id => (
                          <tr key={id.identification_id} style={{ borderBottom: '1px solid var(--c-bg-muted-2)' }}>
                            <td style={{ ...tdStyle, padding: '6px 10px' }}>{id.identification_date ? new Date(id.identification_date).toLocaleDateString() : '—'}</td>
                            <td style={{ ...tdStyle, padding: '6px 10px', fontStyle: 'italic' }} dir="ltr">{id.taxon_name || '—'}</td>
                            <td style={{ ...tdStyle, padding: '6px 10px' }}>{id.taxonomic_rank ? t(`common:rank.${id.taxonomic_rank}`) : '—'}</td>
                            <td style={{ ...tdStyle, padding: '6px 10px' }}>{id.confidence ? t(`common:confidence.${id.confidence}`) : '—'}</td>
                            <td style={{ ...tdStyle, padding: '6px 10px' }}>{id.identification_method ? t(`common:identMethod.${id.identification_method}`) : '—'}</td>
                            <td style={{ ...tdStyle, padding: '6px 10px', color: id.expert_approval ? 'var(--c-success-fg)' : 'var(--c-text-faint-fg)' }}>
                              {id.expert_approval ? t('detail.approvedYes') : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  {/* The newest identification's scientific profile: all four
                      buckets, labelled unconfirmed until it is verified. */}
                  {!detailView.identLoading && detailView.identHistory[0]?.taxon_name && (
                    <AttachedBioProfile stored={detailView.identHistory[0].bioprofile}
                                        verified={Boolean(detailView.identHistory[0].verified)}
                                        name={detailView.identHistory[0].taxon_name} user={user}
                                        testId="collection-ident-bioprofile" />
                  )}
                </SubSection>

                <RelatedRecords t={t} family={detailView.family} />

                {/* Delete: at the foot, away from the × (DangerZone), and only
                    when GET /collections/{id} says this caller may
                    (routes/ownership.py). Editing keeps its own gate. */}
                {detailView.detail.actions?.delete && (
                  <DangerZone testId="detail-delete" label={t('detail.deleteBtn')} title={t('tip.deleteCollection')}
                    confirmTitle={t('detail.deleteConfirmTitle')}
                    consequences={[t('detail.deleteConsequence1'), t('detail.deleteConsequence2')]}
                    failedText={t('detail.deleteFailed')} onConfirm={detailView.deleteCollection} />
                )}
              </>
            )}

            {detailView.detail && detailView.editing && (
              <form onSubmit={detailView.submitEdit}>
                {detailView.saveError && <p role="alert" style={{ color: 'var(--c-danger-fg)', margin: '0 0 12px' }}>{detailView.saveError}</p>}
                <div style={gridStyle}>
                  <label style={labelStyle}>
                    {t('editForm.qrUid')}
                    <QRCodeField
                      value={detailView.editForm.qr_uid}
                      onChange={v => detailView.setEditForm(f => ({ ...f, qr_uid: v }))}
                      initials={user?.initials}
                      autoGenerateIfEmpty={false}
                    />
                  </label>
                  <label style={labelStyle}>{t('editForm.substrate')}
                    <select name="substrate" title={t('tip.substrate')} value={detailView.editForm.substrate} onChange={detailView.onEditChange} style={inputStyle}>
                      <option value="">{t('common:unchangedPlaceholder')}</option>
                      {SUBSTRATES.map(s => <option key={s} value={s}>{t(`substrate.${s}`)}</option>)}
                    </select>
                  </label>
                  <label style={labelStyle}>{t('editForm.fruitingBodySize')}
                    <select name="fruiting_body_size" title={t('tip.size')} value={detailView.editForm.fruiting_body_size} onChange={detailView.onEditChange} style={inputStyle}>
                      <option value="">{t('common:unchangedPlaceholder')}</option>
                      {SIZES.map(s => <option key={s} value={s}>{t(`size.${s}`)} ({t(`sizeHint.${s}`)})</option>)}
                    </select>
                  </label>
                  <label style={{ ...labelStyle, flexDirection: 'row', alignItems: 'center', gap: 6, paddingBottom: 6 }}>
                    <input type="checkbox" name="fruiting_body_size_estimated" title={t('tip.sizeEstimated')} checked={detailView.editForm.fruiting_body_size_estimated} onChange={detailView.onEditChange} />
                    {t('step3.sizeEstimateLabel')}
                  </label>
                  <label style={labelStyle}>{t('editForm.phenologicalStage')}
                    <select name="phenological_stage" title={t('tip.stage')} value={detailView.editForm.phenological_stage} onChange={detailView.onEditChange} style={inputStyle}>
                      <option value="">{t('common:unchangedPlaceholder')}</option>
                      {STAGES.map(s => <option key={s} value={s}>{t(`stage.${s}`)} ({t(`stageHint.${s}`)})</option>)}
                    </select>
                  </label>
                  <label style={labelStyle}>{t('editForm.count')}
                    <input name="fruiting_body_count" title={t('tip.count')} type="number" min="1" step="1" value={detailView.editForm.fruiting_body_count} onChange={detailView.onEditChange} style={inputStyle} />
                  </label>
                  <label style={labelStyle}>{t('editForm.growthPattern')}
                    <select name="growth_pattern" title={t('tip.growthPattern')} value={detailView.editForm.growth_pattern} onChange={detailView.onEditChange} style={inputStyle}>
                      <option value="">{t('common:unchangedPlaceholder')}</option>
                      {GROWTH_PATTERNS.map(g => <option key={g} value={g}>{t(`growthPattern.${g}`)}</option>)}
                    </select>
                  </label>
                  <label style={labelStyle}>{t('editForm.region')}
                    <div style={{ position: 'relative' }}>
                      <input
                        data-testid="edit-region"
                        title={t('tip.region')}
                        value={detailView.editRegion.query}
                        onChange={detailView.editRegion.onChange}
                        onFocus={() => { if (detailView.editRegion.suggestions.length > 0) detailView.editRegion.show(); }}
                        onBlur={() => setTimeout(() => {
                          // reset() closes the list as well as putting the text
                          // back to the region that was actually chosen.
                          detailView.editRegion.reset(detailView.lastConfirmedEditRegionRef.current.query);
                          detailView.setEditForm(f => ({ ...f, region: detailView.lastConfirmedEditRegionRef.current.value }));
                        }, 150)}
                        placeholder={t('common:unchangedPlaceholder')}
                        style={inputStyle}
                        autoComplete="off"
                        dir="rtl"
                      />
                      <SuggestionList box={detailView.editRegion} testId="edit-region-suggestion"
                        onPick={detailView.onEditRegionPick}>{regionRow}</SuggestionList>
                    </div>
                    {detailView.editRegion.noMatch && detailView.editRegion.query.length >= 2 && (
                      <span style={{ display: 'block', marginTop: 5, fontSize: 12, color: 'var(--c-danger-fg)' }}>
                        {t('step2.regionNoMatch')}
                      </span>
                    )}
                    {canEdit && detailView.editRegion.noMatch && detailView.editRegion.query.length >= 2 && !detailView.addingRegion && (
                      <button type="button" onClick={() => detailView.setAddingRegion(true)} onMouseDown={e => e.preventDefault()} title={t('tip.addRegion')}
                        style={{ display: 'block', marginTop: 5, fontSize: 12, color: 'var(--c-primary-fg)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>
                        {t('step2.addRegionPrompt', { query: detailView.editRegion.query })}
                      </button>
                    )}
                    {detailView.addingRegion && (
                      <AddRegionForm t={t} initialQuery={detailView.editRegion.query} seqRef={detailView.editRegionSeqRef}
                        onPicked={detailView.onEditRegionPick}
                        onClose={() => detailView.setAddingRegion(false)} />
                    )}
                    {detailView.editRegion.failed && (
                      <span style={{ display: 'block', marginTop: 5, fontSize: 12, color: 'var(--c-danger-fg)' }}>
                        {t('step2.regionSearchError')}
                      </span>
                    )}
                  </label>
                  <label style={labelStyle}>{t('editForm.gpsLat')}<input name="gps_lat" title={t('tip.gpsLat')} type="number" step="any" value={detailView.editForm.gps_lat} onChange={detailView.onEditChange} style={inputStyle} /></label>
                  <label style={labelStyle}>{t('editForm.gpsLng')}<input name="gps_lng" title={t('tip.gpsLng')} type="number" step="any" value={detailView.editForm.gps_lng} onChange={detailView.onEditChange} style={inputStyle} /></label>
                  <label style={labelStyle}>{t('editForm.labStatus')}
                    <select name="lab_status" title={t('tip.labStatus')} value={detailView.editForm.lab_status} onChange={detailView.onEditChange} style={inputStyle}>
                      <option value="">{t('common:unchangedPlaceholder')}</option>
                      {LAB_STATUSES.map(s => <option key={s} value={s}>{t(`labStatus.${s}`)}</option>)}
                    </select>
                  </label>
                  <label style={labelStyle}>{t('editForm.taxonName')}<input name="current_taxon_name" title={t('tip.taxonName')} value={detailView.editForm.current_taxon_name} onChange={detailView.onEditChange} style={inputStyle} dir="ltr" /></label>
                  <label style={labelStyle}>{t('editForm.taxonRank')}
                    <select name="current_rank" title={t('tip.rank')} value={detailView.editForm.current_rank} onChange={detailView.onEditChange} style={inputStyle}>
                      <option value="">{t('common:unchangedPlaceholder')}</option>
                      {RANKS.map(r => <option key={r} value={r}>{t(`common:rank.${r}`)}</option>)}
                    </select>
                  </label>
                  <label style={labelStyle}>{t('editForm.confidence')}
                    <select name="current_confidence" title={t('tip.confidence')} value={detailView.editForm.current_confidence} onChange={detailView.onEditChange} style={inputStyle}>
                      <option value="">{t('common:unchangedPlaceholder')}</option>
                      {CONFIDENCE.map(c => <option key={c} value={c}>{t(`common:confidence.${c}`)}</option>)}
                    </select>
                  </label>
                  <label style={{ ...labelStyle, gridColumn: 'span 2' }}>{t('editForm.generalNotes')}
                    <textarea name="general_notes" title={t('tip.generalNotes')} value={detailView.editForm.general_notes} onChange={detailView.onEditChange} rows={3} style={inputStyle} />
                  </label>
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button type="submit" disabled={detailView.saving} data-testid="edit-save" title={t('tip.saveEdit')} style={btnStyle('var(--c-primary)')}>{detailView.saving ? t('common:saving') : t('editForm.saveChanges')}</button>
                  <button type="button" onClick={() => detailView.setEditing(false)} title={t('tip.cancelEdit')} style={btnStyle('var(--c-text-muted)')}>{t('common:cancel')}</button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
